// src/pages/admin/AdminBrands.js
// Catalogue des marques : nom + logo, gere par l'admin, propose ensuite aux
// marchands sous forme de liste dans le formulaire d'annonce.
//
// Pourquoi ce catalogue existe : en saisie libre, la meme marque arrivait en
// base sous plusieurs orthographes, ce qui rend tout filtre ou regroupement
// impossible et empeche d'afficher un logo.
// react-icons, aucun emoji. Maxula

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiTag,
  FiUpload,
  FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';

const emptyForm = {
  id: null,
  name: '',
  nameAr: '',
  website: '',
  sortOrder: 0,
  isActive: true,
  logoFile: null,
  logoPreview: null,
  removeLogo: false
};

const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);      // formulaire (creation ou edition)
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);
  const previewRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // L'URL d'apercu est liberee au demontage : sans ca, chaque logo
      // previsualise reste en memoire jusqu'au rechargement de la page.
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      // `all=1` : l'admin voit aussi les marques desactivees.
      const res = await API.get('/brands', { params: { all: 1 } });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      if (mountedRef.current) setBrands(list);
    } catch (e) {
      toast.error('Erreur lors du chargement des marques');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => String(b.name || '').toLowerCase().includes(q));
  }, [brands, search]);

  const openCreate = () => setModal({ ...emptyForm });

  const openEdit = (b) => setModal({
    ...emptyForm,
    id: b.id,
    name: b.name || '',
    nameAr: b.nameAr || '',
    website: b.website || '',
    sortOrder: Number(b.sortOrder) || 0,
    isActive: b.isActive !== false,
    logoPreview: b.logo ? normalizeImagePath(b.logo) : null
  });

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      return toast.error('Logo trop lourd (5 Mo maximum)');
    }
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setModal((m) => ({ ...m, logoFile: file, logoPreview: url, removeLogo: false }));
  };

  const save = async () => {
    if (saving || !modal) return;
    const name = modal.name.trim();
    if (!name) return toast.error('Le nom de la marque est requis');

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('nameAr', modal.nameAr.trim());
      fd.append('website', modal.website.trim());
      fd.append('sortOrder', String(parseInt(modal.sortOrder, 10) || 0));
      fd.append('isActive', modal.isActive ? 'true' : 'false');
      if (modal.logoFile) fd.append('logo', modal.logoFile);
      if (modal.removeLogo) fd.append('removeLogo', 'true');

      if (modal.id) await API.put(`/brands/${modal.id}`, fd);
      else await API.post('/brands', fd);

      toast.success(modal.id ? 'Marque mise à jour' : 'Marque créée');
      setModal(null);
      await fetchBrands();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const toggle = async (b) => {
    try {
      const res = await API.patch(`/brands/${b.id}/toggle`);
      toast.success(res?.data?.message || 'Statut modifié');
      await fetchBrands();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur');
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const res = await API.delete(`/brands/${confirmDelete.id}`);
      toast.success(res?.data?.message || 'Marque supprimée');
      setConfirmDelete(null);
      await fetchBrands();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const Logo = ({ src, size = 44 }) => (
    src ? (
      <img
        src={src}
        alt=""
        className="rounded object-contain bg-white border border-gray-100"
        style={{ width: size, height: size }}
        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
      />
    ) : (
      <span
        className="inline-flex items-center justify-center rounded bg-gray-100 text-gray-400"
        style={{ width: size, height: size }}
      >
        <FiTag size={Math.round(size * 0.5)} />
      </span>
    )
  );

  return (
    <div className="p-6">
      {/* ===== Formulaire ===== */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {modal.id ? 'Modifier la marque' : 'Nouvelle marque'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={modal.name}
                  maxLength={120}
                  onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))}
                  placeholder="Ex : Samsung"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom en arabe <span className="text-gray-400 font-normal">(facultatif)</span>
                </label>
                <input
                  type="text"
                  dir="rtl"
                  value={modal.nameAr}
                  maxLength={120}
                  onChange={(e) => setModal((m) => ({ ...m, nameAr: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  style={{ fontFamily: "'Cairo', Arial, sans-serif" }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                <div className="flex items-center gap-4">
                  <Logo src={modal.removeLogo ? null : modal.logoPreview} size={64} />
                  <div className="flex flex-col gap-2">
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
                      <FiUpload size={15} />
                      Choisir un fichier
                      <input type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
                    </label>
                    {modal.logoPreview && !modal.removeLogo && (
                      <button
                        type="button"
                        onClick={() => setModal((m) => ({ ...m, removeLogo: true, logoFile: null, logoPreview: null }))}
                        className="inline-flex items-center gap-2 text-sm text-red-600 hover:underline"
                      >
                        <FiX size={14} /> Retirer le logo
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  PNG sur fond transparent de préférence, 5 Mo maximum. Le logo s'affiche
                  dans la liste que voient les marchands et sur la fiche produit.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site web <span className="text-gray-400 font-normal">(facultatif)</span>
                  </label>
                  <input
                    type="text"
                    value={modal.website}
                    onChange={(e) => setModal((m) => ({ ...m, website: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ordre d'affichage
                  </label>
                  <input
                    type="number"
                    value={modal.sortOrder}
                    onChange={(e) => setModal((m) => ({ ...m, sortOrder: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Plus petit = plus haut dans la liste. Égalité : ordre alphabétique.
                  </p>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={modal.isActive}
                  onChange={(e) => setModal((m) => ({ ...m, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm text-gray-700">
                  Active — visible dans la liste des marchands
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                disabled={saving}
                onClick={() => setModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={save}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Suppression ===== */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Supprimer « {confirmDelete.name} » ?
            </h3>
            <p className="text-sm text-gray-600">
              {confirmDelete.dealsCount > 0 ? (
                <>
                  {confirmDelete.dealsCount} annonce(s) utilisent cette marque. Elles ne
                  seront pas supprimées et garderont le nom « {confirmDelete.name} » en
                  texte, mais perdront le logo et le lien vers la fiche marque.
                </>
              ) : (
                <>Aucune annonce n'utilise cette marque.</>
              )}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Si vous voulez seulement la retirer de la liste des marchands,
              <strong> désactivez-la</strong> plutôt que de la supprimer.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={doDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marques</h1>
          <p className="text-gray-600 mt-1">
            Les marchands choisissent dans cette liste au lieu de saisir le nom à la main.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 inline-flex items-center gap-2"
        >
          <FiPlus /> Nouvelle marque
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une marque..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marque
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Annonces
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                  </td>
                </tr>
              ) : filtered.length ? (
                filtered.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Logo src={b.logo ? normalizeImagePath(b.logo) : null} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{b.name}</div>
                          {b.nameAr && (
                            <div
                              dir="rtl"
                              className="text-xs text-gray-500 truncate"
                              style={{ fontFamily: "'Cairo', Arial, sans-serif" }}
                            >
                              {b.nameAr}
                            </div>
                          )}
                          {!b.logo && (
                            <div className="text-xs text-amber-600 mt-0.5">Aucun logo</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{b.dealsCount ?? 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{b.sortOrder ?? 0}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          b.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {b.isActive ? 'Active' : 'Désactivée'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => toggle(b)}
                          className="p-2 text-gray-500 hover:text-primary-600"
                          title={b.isActive ? 'Désactiver' : 'Activer'}
                        >
                          {b.isActive ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(b)}
                          className="p-2 text-gray-500 hover:text-primary-600"
                          title="Modifier"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(b)}
                          className="p-2 text-gray-500 hover:text-red-600"
                          title="Supprimer"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <FiTag className="mx-auto text-4xl mb-2" />
                    <p>Aucune marque pour le moment</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminBrands;
